import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form as AntForm,
  Input,
  Typography,
  Row,
  Col,
  Button,
  message,
  Spin,
  Space,
  Radio,
  Alert,
  Divider,
  Grid,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import {
  DeleteOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import { postConsultaRepuveNacional } from 'api/recaudacion/repuve';
import { getColorVehiculo, getVehiculoCaja } from 'api/recaudacion';
import { getVersionVehicular } from 'api/catalogos/versionVehicular';
import styled from 'styled-components';
import address from 'utils/address';
import { getContribuyenteCaja } from 'api/empresas/contribuyentes';
import { toUpper } from 'utils/normalizers';
import { getLineaDeVehiculo } from 'api/recaudacion/lineasVehiculares';
import { getTipoDeVehiculo } from 'api/recaudacion/tiposDeVehiculos';
import { getProcedenciaDeVehiculo, getUsoDeMotocicleta } from 'api/catalogos';
import { getMarcaDeVehiculo } from 'api/recaudacion/marcasVehiculares';
import { propsRpvGeneral, propsRpvProcedencia, propsRpv } from '../repuve';
import v from '../configsPadrones/vehiculos';

const fieldsReporteRobo = [
  { title: 'Reporte de robo' },
  { name: 'doConsRepRobo_Id_estatus', label: 'Id estatus' },
  { name: 'doConsRepRobo_Fecha_robo', label: 'Fecha' },
  { name: 'doConsRepRobo_Placa', label: 'Placa' },
  { name: 'doConsRepRobo_NIV', label: 'NIV' },
  { name: 'doConsRepRobo_Fuente_robo', label: 'Fuente' },
  { name: 'doConsRepRobo_Averiguacion', label: 'Averiguación' },
  { name: 'doConsRepRobo_Agente_MP', label: 'Agente MP' },
  { name: 'doConsRepRobo_Fecha_averiguacion', label: 'Fecha averiguación' },
];

const rpvPropToFieldConfig = ([name, localName]) => ({
  name,
  localName,
  label: v.configs[localName]?.formItemProps?.label || v.configs[localName]?.resultProps?.label,
  excludeCompare: /doConsRpv_Observaciones|doConsRpv_Estatus_registro/.test(name),
});

const fields = [
  { title: 'Información del vehículo' },
  ...propsRpvGeneral.map(rpvPropToFieldConfig),
  ...propsRpvProcedencia.map((e, i) => ({
    ...rpvPropToFieldConfig(e),
    isVisible: i
      ? (values) => !!values.doConsRpv_Pedimento || [2, 3].includes(values.procedencia_vehiculo)
      : undefined,
  })),
  { name: 'doConsRpv_Folio_CI', label: 'Folio CI', excludeCompare: true },
  { title: 'Información de Propietario' },
  { textarea: true, name: 'nombre_completo', label: 'Nombre' },
  // eslint-disable-next-line object-curly-newline
  { textarea: true, name: 'direccion', label: 'Dirección', excludeCompare: true },
  { name: 'doConsPropietario_RFC', label: 'RFC' },
  { name: 'doConsPropietario_CURP', label: 'CURP' },
  { name: 'doConsPropietario_Licencia', label: 'Licencia', excludeCompare: true },
  { name: 'doConsPropietario_Pasaporte', label: 'Pasaporte', excludeCompare: true },
];

const addressProps = [
  'doConsPropietario_Calle',
  'doConsPropietario_Colonia',
  'doConsPropietario_Municipio_delegacion',
  'doConsPropietario_CP',
  'doConsPropietario_Entidad_federativa_propietario',
];

const fullnameProps = [
  'doConsPropietario_Nombre',
  'doConsPropietario_Apellido_paterno',
  'doConsPropietario_Apellido_materno',
];

export const composeNameFromRepuve = (values) => fullnameProps.map((p) => values[p]).filter((e) => e).join(' ');

export const getAddressRepuve = (_values) => {
  const values = addressProps.map((p) => _values[p]);
  values[3] = values[3] ? `C.P. ${values[3]}` : null;
  return values.filter((e) => e).join(', ');
};

const normalizeValues = (values) => ({
  ...values,
  direccion: getAddressRepuve(values),
  nombre_completo: composeNameFromRepuve(values),
});

const isDistinct = (_name, vehiculoStrings, repuveValues) => {
  const [rpvName, localName] = propsRpv
    .find((names) => names.includes(Array.isArray(_name) ? _name[1] : _name)) || [];
  const _isDiscinct = vehiculoStrings[localName]?.toString() !== repuveValues[rpvName]?.toString()
    && (vehiculoStrings[localName] || repuveValues[rpvName]);
  return _isDiscinct;
};

const renderField = (f) => (f.title ? (
  <Col span={24} key={f.title}>
    <Typography.Title level={3} style={f.title === 'Información de Propietario' ? { marginTop: '.5em' } : null}>
      {f.title}
    </Typography.Title>
  </Col>
) : (
  <Col xs={24} sm={24} md={f.textarea ? 12 : 8} key={f.name}>
    <Form.Item name={f.name} label={f.label}>
      {f.textarea
        ? <Input.TextArea autoSize={{ minRows: 2 }} disabled />
        : <Input disabled />}
    </Form.Item>
  </Col>
));

export const hasReporteRobo = (values = {}) => !!values?.doConsRepRobo_Id_estatus;

const ModalInfoRepuve = ({
  values: initialValues,
  visible,
  onFinish,
  onFinishText,
  onCancel,
  allowSearch,
  modal,
  plantillasDeTramites,
}) => {
  const screens = Grid.useBreakpoint();
  const [formInstance] = AntForm.useForm();
  const es_agencia = AntForm.useWatch('es_agencia', formInstance);
  const es_reemplazo_chip = AntForm.useWatch('es_reemplazo_chip', formInstance);
  const es_cambio_de_propietario = AntForm.useWatch('es_cambio_de_propietario', formInstance);
  const es_actualizacion_info = AntForm.useWatch('es_actualizacion_info', formInstance);
  const es_actualizacion_placa = AntForm.useWatch('es_actualizacion_placa', formInstance);
  const solicitante = AntForm.useWatch('solicitante', formInstance);
  const [repuveValues, setRepuveValues] = useState();
  const [vehiculo, setVehiculo] = useState();
  const [vehiculoStrings, setVehiculoStrings] = useState();
  const [messageWS, setMessageWS] = useState();
  const [dataSourceSolicitante, setDataSourceSolicitante] = useState([]);
  const [loading, setLoading] = useState(false);

  const distinctInfo = useMemo(
    () => (vehiculoStrings && repuveValues ? fields.filter((f) => !f.title && !f.excludeCompare)
      .some((f) => isDistinct(f.name, vehiculoStrings, repuveValues)) : false),
    [vehiculoStrings, repuveValues],
  );

  const solicitanteSelected = dataSourceSolicitante.find((e) => e.id === solicitante);
  const propietarioNacional = dataSourceSolicitante.find((e) => e.nacional);
  const propietarioLocal = dataSourceSolicitante.find((e) => e.local);

  const propietariosDiferentes = propietarioLocal && propietarioNacional
  && propietarioNacional.rfc !== propietarioLocal.rfc;

  const fieldsAlta = [es_agencia, es_reemplazo_chip];
  const fieldsModificacion = [
    es_cambio_de_propietario,
    es_actualizacion_info,
    es_actualizacion_placa,
    es_reemplazo_chip,
  ];
  const plantilla = useMemo(
    () => {
      const _fields = vehiculo ? fieldsModificacion : fieldsAlta;
      if (_fields.some((e) => typeof e !== 'number')) return null;
      const match = plantillasDeTramites.find(({ nombre }) => {
        if (vehiculo && (/con registro|inscripción/i.test(nombre) || /^registro|inscripción/i.test(nombre))) return false;
        if ((es_agencia && !/agencia/i.test(nombre)) || (!es_agencia && /agencia/i.test(nombre))) return false;
        if ((es_reemplazo_chip && !/(^|con )r(e|ee)mplazo de chip/i.test(nombre))
          || (!es_reemplazo_chip && /(^|con )r(e|ee)mplazo de chip/i.test(nombre))) return false;
        if ((es_cambio_de_propietario && !/cambio de propietario/i.test(nombre))
          || (!es_cambio_de_propietario && /cambio de propietario/i.test(nombre))) return false;
        if ((es_actualizacion_placa && !/actualización de placa/i.test(nombre))
          || (!es_actualizacion_placa && /actualización de placa/i.test(nombre))) return false;
        if ((es_actualizacion_info && !/actualización de datos/i.test(nombre))
          || (!es_actualizacion_info && /actualización de datos/i.test(nombre))) return false;
        return true;
      });
      return match;
    },
    [
      es_agencia,
      es_reemplazo_chip,
      es_cambio_de_propietario,
      es_actualizacion_info,
      es_actualizacion_placa,
    ],
  );

  const esCambio = /cambio de propietario/i.test(plantilla?.nombre);

  const clear = () => {
    formInstance.resetFields();
    setRepuveValues();
    setVehiculo();
    setVehiculoStrings();
    setMessageWS();
    setDataSourceSolicitante([]);
  };

  useEffect(() => {
    if (initialValues) {
      const _values = normalizeValues(initialValues);
      formInstance.setFieldsValue(_values);
      setRepuveValues(_values);
    } else {
      clear();
    }
  }, [initialValues]);

  const onClickSearch = async () => {
    message.destroy();
    const _params = formInstance.getFieldsValue(['niv', 'numero_de_placa']);
    if (!_params.niv && !_params.numero_de_placa) {
      message.warn('Ingrese al menos un campo');
      return;
    }
    const params = {
      numero_de_placa_vigente: _params.numero_de_placa,
      numero_de_identificacion_vehicular: _params.niv,
    };
    setLoading(true);
    const _repuveValues = await postConsultaRepuveNacional(params);
    const [_vehiculo] = await getVehiculoCaja(params);
    const _vehiculoStrings = { ..._vehiculo };
    if (_vehiculo) {
      const existingKeys = [
        'version_vehicular',
        'color_del_vehiculo',
        'tipo_de_vehiculo',
        'uso_del_vehiculo',
        'procedencia_vehiculo',
      ].filter((key) => _vehiculo[key]);
      const requests = [];
      if (_vehiculo.version_vehicular) {
        requests.push(getVersionVehicular(_vehiculo.version_vehicular));
      }
      if (_vehiculo.color_del_vehiculo) {
        requests.push(getColorVehiculo(_vehiculo.color_del_vehiculo));
      }
      if (_vehiculo.tipo_de_vehiculo) {
        requests.push(getTipoDeVehiculo(_vehiculo.tipo_de_vehiculo));
      }
      if (_vehiculo.uso_del_vehiculo) {
        requests.push(getUsoDeMotocicleta(_vehiculo.uso_del_vehiculo));
      }
      if (_vehiculo.procedencia_vehiculo) {
        requests.push(getProcedenciaDeVehiculo(_vehiculo.procedencia_vehiculo));
      }
      (await Promise.all(requests)).forEach((e, idx) => {
        _vehiculoStrings[existingKeys[idx]] = e.descripcion || e.nombre;
      });
      const linea = await getLineaDeVehiculo(_vehiculo.linea);
      _vehiculoStrings.linea = linea.descripcion || linea.nombre;
      const marca = await getMarcaDeVehiculo(linea.marca);
      _vehiculoStrings.marca = marca.descripcion || marca.nombre;
    }
    let _messageWS;
    if (_repuveValues === null) {
      _messageWS = 'No pudo realizarse consulta a la Base de Datos Nacional';
    } else if (_repuveValues === false) {
      _messageWS = 'El vehículo ingresado No existe en la Base de Datos Nacional';
    }
    if (_vehiculo?.contribuyente_propietario?.direcciones) {
      _vehiculo.contribuyente_propietario
        .direccion_str = address(_vehiculo.contribuyente_propietario.direcciones);
    }
    const normalized = _repuveValues ? normalizeValues(_repuveValues) : null;
    formInstance.setFieldsValue({
      ...normalized,
      vehiculo: _vehiculoStrings,
    });
    const _dataSourceSolicitante = [{ id: 'sin_solicitante', nombre_completo: 'Buscar más  adelante' }];
    if (_vehiculo) {
      _dataSourceSolicitante.push({
        ..._vehiculo.contribuyente_propietario,
        local: true,
      });
    }
    if (_repuveValues
      && _repuveValues.doConsPropietario_RFC !== _vehiculo?.contribuyente_propietario?.rfc) {
      const { results: [_contribuyente] } = await getContribuyenteCaja({
        q: _repuveValues?.doConsPropietario_RFC,
      });
      if (_contribuyente) {
        _contribuyente.nacional = true;
        _contribuyente.registrado = true;
      }
      _dataSourceSolicitante.push(_contribuyente || {
        id: 'not_found',
        nombre_completo: composeNameFromRepuve(_repuveValues),
        rfc: _repuveValues?.doConsPropietario_RFC,
        nacional: true,
        registrado: false,
      });
    }
    if (!_vehiculo && !_repuveValues) formInstance.setFieldsValue({ solicitante: 'sin_solicitante' });
    if (!_vehiculo) setLoading(false);
    setMessageWS(_messageWS);
    setVehiculo(_vehiculo);
    setVehiculoStrings(_vehiculoStrings);
    setRepuveValues(_repuveValues);
    setDataSourceSolicitante(_dataSourceSolicitante);
    setLoading(false);
  };

  const _onCancel = () => {
    onCancel();
    clear();
  };

  const onClick = async () => {
    setLoading(true);
    await onFinish(
      plantilla,
      repuveValues,
      vehiculo,
      typeof solicitante === 'number' ? solicitanteSelected : null,
    );
    setLoading(false);
  };

  const getAction = () => {
    if (repuveValues === null) {
      return (
        <Button onClick={onClickSearch}>
          Reintentar
          <ReloadOutlined />
        </Button>
      );
    }
    return undefined;
  };

  const reset = () => formInstance.resetFields([
    'es_agencia',
    'es_reemplazo_chip',
    'es_cambio_de_propietario',
    'es_actualizacion_info',
    'es_actualizacion_placa',
  ]);

  const getIsDisbaledReemplazo = () => {
    if (vehiculo) {
      if ([es_cambio_de_propietario, es_actualizacion_info, es_actualizacion_placa]
        .every((e) => e === 0)) return false;
      return !(es_cambio_de_propietario || es_actualizacion_placa);
    }
    return es_agencia !== 0;
  };

  const Content = () => (
    <Spin spinning={loading}>
      <Form
        form={formInstance}
        onFinish={repuveValues === undefined ? onClickSearch : onClick}
        layout="vertical"
        style={{ width: '100%' }}
        onValuesChange={(changed, allValues) => {
          if (changed.es_agencia) formInstance.setFieldsValue({ es_reemplazo_chip: 0 });
          if (changed.es_reemplazo_chip) {
            formInstance.setFieldsValue({ es_actualizacion_info: 0, es_agencia: 0 });
          }
          if (changed.es_cambio_de_propietario) {
            formInstance.setFieldsValue({
              es_actualizacion_info: 0,
              es_actualizacion_placa: 0,
            });
          }
          if (changed.es_actualizacion_info) {
            formInstance.setFieldsValue({
              es_cambio_de_propietario: 0,
              es_reemplazo_chip: 0,
              es_actualizacion_placa: 0,
            });
          }
          if (changed.es_actualizacion_placa) {
            formInstance.setFieldsValue({
              es_cambio_de_propietario: 0,
              es_actualizacion_info: 0,
            });
          }
          if (allValues.es_cambio_de_propietario
            && (!solicitanteSelected || solicitanteSelected?.nacional)) {
            formInstance.setFieldsValue({ solicitante: 'sin_solicitante' });
          }
        }}
        $modal={modal}
      >
        <Row gutter={[10, 10]}>
          {allowSearch && (
            <>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="niv"
                  label="Número de Identificación Vehicular"
                  className="margin"
                  normalize={toUpper}
                >
                  <Input disabled={repuveValues !== undefined} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="numero_de_placa"
                  label="Número de Placa Vigente"
                  className="margin"
                  normalize={toUpper}
                >
                  <Input disabled={repuveValues !== undefined} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item style={{ marginTop: 22 }} className="margin">
                  <Space>
                    <Button htmlType="submit" disabled={repuveValues !== undefined}>
                      Buscar
                      <SearchOutlined />
                    </Button>
                    <Button onClick={clear}>
                      Limpiar
                      <DeleteOutlined />
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </>
          )}
          {repuveValues !== undefined && !modal && (
            <Col span={24}>
              <Divider orientation="left">
                <Typography.Title level={2}>
                  Registro en Base de Datos Local
                </Typography.Title>
              </Divider>
            </Col>
          )}
          {vehiculo && (
            <>
              <Col span={24}>
                <Typography.Title level={3}>
                  Información del Vehículo
                </Typography.Title>
              </Col>
              {fields.filter((f) => f.localName && (!f.isVisible || f.isVisible(vehiculo)))
                .map((f) => renderField({ name: ['vehiculo', f.localName], label: f.label }))}
              <Col span={24}>
                <Typography.Title level={3}>
                  Información de Propietario
                </Typography.Title>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name={['vehiculo', 'contribuyente_propietario', 'nombre_completo']} label="Nombre">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name={['vehiculo', 'contribuyente_propietario', 'direccion_str']} label="Dirección">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item name={['vehiculo', 'contribuyente_propietario', 'rfc']} label="RFC">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item name={['vehiculo', 'contribuyente_propietario', 'curp']} label="CURP">
                  <Input disabled />
                </Form.Item>
              </Col>
            </>
          )}
          {repuveValues !== undefined && !vehiculo && !modal && (
            <Col span={24}>
              <Alert message="Vehículo no registrado en base de datos local" type="warning" showIcon />
            </Col>
          )}
          {repuveValues !== undefined && !modal && (
            <Col span={24} style={{ marginTop: 20 }}>
              <Divider orientation="left">
                <Typography.Title level={2}>
                  Registro en Base de Datos Nacional
                </Typography.Title>
              </Divider>
            </Col>
          )}
          {(messageWS || repuveValues?.Error_Descripcion) && (
            <Col span={24}>
              <Alert
                showIcon
                message={(
                  <>
                    {messageWS || repuveValues?.Error_Descripcion}
                    {!screens.md ? getAction() : null}
                  </>
                )}
                type="warning"
                action={screens.md ? getAction() : null}
              />
            </Col>
          )}
          {!!repuveValues && (
            <>
              {hasReporteRobo(repuveValues)
                && fieldsReporteRobo.map((f) => renderField(f))}
              {fields.filter((f) => !f.isVisible || f.isVisible(repuveValues))
                .map((f) => renderField(f))}
            </>
          )}
          {repuveValues !== undefined && !modal && (
            <>
              {!!distinctInfo && (
                <Col span={24}>
                  <Alert
                    showIcon
                    type="warning"
                    message="Se detecta información diferente entre las bases de datos"
                    description={(
                      <>
                        Verifique la información, si es incorrecta en base de datos local
                        deben realizarse las correcciones necesarias mediante el trámite
                        <Button
                          type="link"
                          className="no-color"
                          onClick={() => formInstance.setFieldsValue({
                            es_actualizacion_info: 1,
                            es_cambio_de_propietario: 0,
                          })}
                        >
                          {plantillasDeTramites.find((p) => /actualización/i.test(p.nombre)).nombre}
                        </Button>
                      </>
                    )}
                  />
                </Col>
              )}
              <Col span={24}>
                <Typography.Title level={3} style={{ marginTop: 35 }}>
                  Responda el formulario a continuación para identificar el trámite a realizar
                </Typography.Title>
              </Col>
              <Col span={24}>
                <Form.Item
                  className="fi-horizontal"
                  label="Limpiar respuestas"
                  name="reset"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Button onClick={reset}>
                    <ReloadOutlined />
                  </Button>
                </Form.Item>
              </Col>
              <Col span={vehiculo ? 0 : 24}>
                <Form.Item
                  className="fi-horizontal"
                  label="¿Es trámite de agencia?"
                  name="es_agencia"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Radio.Group buttonStyle="solid" disabled={!!es_reemplazo_chip}>
                    <Radio.Button value={1}>Si</Radio.Button>
                    <Radio.Button value={0}>No</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={vehiculo ? 24 : 0}>
                <Form.Item
                  className="fi-horizontal"
                  label="¿Es cambio de propietario?"
                  name="es_cambio_de_propietario"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Radio.Group buttonStyle="solid" disabled={!!es_actualizacion_info || !!es_actualizacion_placa}>
                    <Radio.Button value={1}>Si</Radio.Button>
                    <Radio.Button value={0}>No</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={vehiculo ? 24 : 0}>
                <Form.Item
                  className="fi-horizontal"
                  label="¿Es actualización de información?"
                  name="es_actualizacion_info"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Radio.Group buttonStyle="solid" disabled={!!es_cambio_de_propietario || !!es_actualizacion_placa}>
                    <Radio.Button value={1}>Si</Radio.Button>
                    <Radio.Button value={0}>No</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={vehiculo ? 24 : 0}>
                <Form.Item
                  className="fi-horizontal"
                  label="¿Es actualización de placa?"
                  name="es_actualizacion_placa"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Radio.Group buttonStyle="solid" disabled={!!es_cambio_de_propietario || !!es_actualizacion_info}>
                    <Radio.Button value={1}>Si</Radio.Button>
                    <Radio.Button value={0}>No</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  className="fi-horizontal"
                  label="¿Es reemplazo de chip?"
                  name="es_reemplazo_chip"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Radio.Group
                    buttonStyle="solid"
                    disabled={getIsDisbaledReemplazo()}
                  >
                    <Radio.Button value={1}>Si</Radio.Button>
                    <Radio.Button value={0}>No</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Propietario del vehículo"
                  tooltip="Debe ser usado como solicitante del trámite"
                  name="solicitante"
                  rules={[{ required: !esCambio }]}
                >
                  <Radio.Group
                    disabled={!plantilla}
                    style={{ width: '100%' }}
                    options={dataSourceSolicitante.map((s) => ({
                      value: s.id,
                      label: [
                        s.rfc,
                        s.nombre_completo,
                        s.nacional && '(nacional)',
                        s.local && '(local)',
                      ].filter((e) => e).join(' - '),
                      style: { width: '100%' },
                      disabled: esCambio && s.nacional,
                    }))}
                  />
                </Form.Item>
              </Col>
              {solicitanteSelected?.nacional && !solicitanteSelected.registrado && (
                <Alert
                  type="warning"
                  message="Propietario en base de datos nacional no se encuentró en base de datos local, será necesario registrarlo a continuación"
                  showIcon
                />
              )}
              {solicitante === 'sin_solicitante' && (
                <Alert
                  type="info"
                  message="Se continuará sin información de solicitante"
                  description={`Será necesario buscarlo o registrarlo a continuación${esCambio ? ', si el solicitante es diferente al propietario actual se tomará como propietario nuevo' : ''}`}
                  showIcon
                />
              )}
              {propietariosDiferentes && (
                <Alert
                  type="warning"
                  message="Propietario en base de datos local es diferente a propietario en base de datos nacional"
                  description="Verifique la información, si propietario en base de datos nacional es el correcto deben realizarse las correcciones necesarias en base de datos local"
                  showIcon
                />
              )}
              <Col span={24} style={{ marginTop: 20, marginBottom: 20 }}>
                <Typography.Text
                  level={4}
                  style={{ lineHeight: '36px', fontWeight: 'bold', fontSize: 20 }}
                  className={plantilla ? 'success no-color' : ''}
                >
                  {`TRÁMITE: ${plantilla?.nombre || 'POR DEFINIR'}`}
                </Typography.Text>
                <Button
                  className={plantilla ? 'success no-color' : ''}
                  disabled={!plantilla || hasReporteRobo(repuveValues)
                    || (esCambio && solicitanteSelected.nacional)}
                  style={{
                    float: 'right',
                    textTransform: 'uppercase',
                    fontSize: 18,
                    height: 36,
                  }}
                  htmlType="submit"
                >
                  Continuar
                  <RightOutlined />
                </Button>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Spin>
  );

  if (!modal) {
    return <Content />;
  }

  return (
    <Modal
      open={visible}
      closable={false}
      title={(
        <FormSubmitControls
          label="Información consulta REPUVE"
          onFinishText={onFinishText}
          onFinishIcon={<SnippetsOutlined />}
          onFinish={repuveValues && !repuveValues?.Error_Descripcion && onFinish ? onFinish : null}
          onCancel={_onCancel}
          loading={loading}
        />
      )}
      footer={null}
      width="80%"
    >
      <Content />
    </Modal>
  );
};

const Form = styled(AntForm)`
  .fi-horizontal {
    .ant-row.ant-form-item-row {
      flex-direction: row !important;
    }
  }
  .ant-form-item:not(.margin) {
    margin: 0 !important;
  }
  .ant-alert {
    font-weight: bold;
    width: 100%;
  }
  .ant-radio-group {
    .ant-radio-wrapper:hover {
      background: #40a9ff30;
    }
  }

  ${(props) => !props.$modal && `
    .ant-col.ant-form-item-label label {
      height: 16px;
    }
    .ant-col.ant-form-item-label label, .ant-radio-wrapper > span:nth-child(2),
      .ant-alert-description, .ant-form-item-control-input-content input {
      font-size: 16px;
    }
    .ant-alert-message {
      font-size: 18px;
    }
  `}

  .ant-divider-horizontal.ant-divider-with-text {
    border-top-color: rgba(0, 0, 0, .15);
  }

  .ant-divider-horizontal.ant-divider-with-text:after,
  .ant-divider-horizontal.ant-divider-with-text:before {
    border-top-width: 3px;
    border-top-style: solid;
  }
`;

ModalInfoRepuve.propTypes = {
  visible: PropTypes.bool,
  values: PropTypes.shape({
    Error_Descripcion: PropTypes.string,
  }),
  onFinish: PropTypes.func,
  onCancel: PropTypes.func,
  allowSearch: PropTypes.bool,
  onFinishText: PropTypes.string,
  modal: PropTypes.bool,
  plantillasDeTramites: PropTypes.arrayOf(PropTypes.shape({})),
};

ModalInfoRepuve.defaultProps = {
  visible: false,
  values: undefined,
  allowSearch: false,
  onFinish: null,
  onCancel: null,
  onFinishText: null,
  modal: false,
  plantillasDeTramites: [],
};

export default ModalInfoRepuve;
